import PropTypes from 'prop-types';
import { useState, useMemo, useEffect } from 'react';

import { DefaultLink } from 'hb-react/frontoffice/components/Link';
import { useDonationContext } from 'hb-react/frontoffice/contexts/DonationContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ContentPreview from 'hb-react/shared/components/ContentPreview';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { enableScroll } from 'helpers/scroll';

import { dynamicDonationResourcePath } from '../../../../utils/whiteLabel';

const CompanyDetails = ({ companyId }) => {
  const [company, setCompany] = useState({});
  const { currentUser } = useDefaultAppContexts();
  const { whitelabelURL } = useDonationContext();
  const whitelabelURLEnabled = useMemo(
    () => (currentUser ? false : whitelabelURL),
    [currentUser, whitelabelURL],
  );

  // LIFECYCLE

  useEffect(() => {
    const path = dynamicDonationResourcePath(
      Routes.donations_hep_fetch_path({ company_id: companyId }),
      {
        whitelabelURL: whitelabelURLEnabled,
      },
    );

    axios
      .get(path)
      .then((response) => {
        setCompany(response.data || {});
      })
      .catch((_error) => {
        displayNotif(
          'error',
          __('Sorry, this company details can not be found'),
        );
      });
  }, [companyId, whitelabelURLEnabled]);

  // RENDERERS

  const onlineResources = company?.online_resources?.[0];
  const giftratios = company?.giftratios?.[0];

  const renderCompanyArray = () => (
    <div className="soft">
      {renderCompanyRow({ label: __('Name'), value: company.name })}
      {renderCompanyRow({
        label: __('Foundation'),
        value: company.foundation_name,
      })}
      {renderCompanyRow({
        label: __('Gift form'),
        value: onlineResources?.online_formurl,
        isLink: true,
      })}
      {renderCompanyRow({
        label: __('Gift ratio'),
        value: giftratios?.giftratio,
      })}
      {renderCompanyRow({
        label: __('Minimum amount matched'),
        value: giftratios?.minimum_amount_matched,
      })}
      {renderCompanyRow({
        label: __('Maximum amount matched'),
        value: giftratios?.maximum_amount_matched,
      })}
      {renderCompanyRow({
        label: __('Comments'),
        value: company?.comments,
      })}
    </div>
  );

  const renderCompanyRow = ({ label, value, isLink }) => {
    const valueDisplayed =
      value && isLink ? (
        <DefaultLink className="color-blue" target="_blank" href={value}>
          {value}
        </DefaultLink>
      ) : (
        value
      );

    return (
      <div className="flex palm--flex-wrap push--bottom">
        <div className="weight--bold width--150 palm--width--100-percent push-half--right palm--push-quarter--bottom">
          {label}
        </div>
        <div className="flex-grow--1 flex-basis--0">
          {valueDisplayed || '—'}
        </div>
      </div>
    );
  };

  const link = (
    <a className="weight--bold custom-color-links custom-color-button-primary">
      {__('See more info')}
    </a>
  );

  return (
    <ContentPreview
      portalProps={{
        closeOnEsc: true,
        closeOnOutsideClick: false,
        openByClickOn: link,
        onOpen: () => enableScroll(false),
        onClose: () => enableScroll(),
      }}
      size="large"
      title={__('Company details')}
      screenReadersCloseButtonText={s__(
        'Accessibility|Close company details window',
      )}
    >
      {renderCompanyArray()}
    </ContentPreview>
  );
};

CompanyDetails.propTypes = {
  companyId: PropTypes.string.isRequired,
};

export default CompanyDetails;
